import * as RCheckbox from "@radix-ui/react-checkbox";
import { cva, VariantProps } from "class-variance-authority";
import { cx } from "class-variance-authority";
import React, { forwardRef } from "react";
import { FiCheck, FiMinus } from "react-icons/fi";
import InputLabel from "./input-general/input-label";
import { LabelMethod, normalizeLabelProp } from "./input-general/label-prop";
import { useHtmlId } from "./utils/useHtmlId";

interface CheckboxProps
  extends Omit<
    React.HTMLProps<HTMLButtonElement>,
    "checked" | "onChange" | "value" | "type" | "children" | "ref"
  > {
  checked?: RCheckbox.CheckedState;
  onChange?: (checked: RCheckbox.CheckedState) => void;
  size?: 32 | 24 | 20 | 16 | 12 | 8 | 4;
  color?: "magenta" | "navy";
  id?: string;
  ariaLabel?: string; //This is so we can be more specific with the label
}

export const FullSizeCheckbox = ({
  className,
  size,
  checked,
  color,
  onChange,
  disabled,
  ...props
}: CheckboxProps) => {
  const colorClass = color === "magenta" ? "bg-magenta" : "bg-navy";
  return (
    <RCheckbox.Root
      className="flex items-center justify-center"
      style={{ width: "100%", height: "100%" }}
      checked={checked}
      onCheckedChange={(checked) => onChange(checked)}
      disabled={disabled}
      id={props.id}
    >
      <div
        className={cx(
          "flex-shrink-0 overflow-hidden rounded border border-light-candela text-white",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          className,
        )}
        style={{
          width: size || 20,
          height: size || 20,
        }}
      >
        <RCheckbox.Indicator
          // as="div"
          className={cx(
            "h-full w-full",
            "flex items-center justify-center",
            checked === "indeterminate" && colorClass,
            checked === true && colorClass,
          )}
        >
          {checked === "indeterminate" && <FiMinus />}
          {checked === true && <FiCheck />}
        </RCheckbox.Indicator>
      </div>
    </RCheckbox.Root>
  );
};

export const Checkbox = forwardRef<
  HTMLButtonElement,
  CheckboxProps & { checkboxClass?: string }
>(
  (
    {
      className,
      size,
      checked,
      color,
      onChange,
      disabled,
      ...props
    }: CheckboxProps & {
      checkboxClass?: string;
    },
    ref,
  ) => {
    const colorClass = color === "magenta" ? "bg-magenta" : "bg-navy";
    const disabledColorClass =
      color === "magenta" ? "bg-magenta-disabled" : "bg-navy-disabled";
    return (
      <RCheckbox.Root
        ref={ref}
        onClick={props.onClick}
        checked={checked}
        onCheckedChange={(checked) => onChange?.(checked)}
        disabled={disabled}
        id={props.id}
        className={props.checkboxClass}
        style={{
          width: size || 20,
          height: size || 20,
        }}
        tabIndex={props.tabIndex}
        aria-label={props.ariaLabel}
      >
        <div
          className={cx(
            "flex-shrink-0 overflow-hidden rounded border border-light-candela text-white",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            className,
          )}
          style={{
            width: size || 20,
            height: size || 20,
          }}
        >
          <RCheckbox.Indicator
            // as="div"
            className={cx(
              "h-full w-full",
              "flex items-center justify-center",
              checked === "indeterminate" && colorClass,
              checked && (disabled ? disabledColorClass : colorClass),
            )}
          >
            {checked === "indeterminate" && <FiMinus />}
            {checked === true && <FiCheck />}
          </RCheckbox.Indicator>
        </div>
      </RCheckbox.Root>
    );
  },
);

const checkboxStyles = cva("flex flex-row gap-x-2", {
  variants: {
    color: {
      magenta: "",
      navy: "",
    },
  },
  defaultVariants: {
    color: "magenta",
  },
});

interface LabeledCheckboxProps
  extends Omit<CheckboxProps, "value" | "onChange" | "label">,
    VariantProps<typeof checkboxStyles> {
  label: LabelMethod | string;
  value: boolean;
  onChange?: (v: boolean) => void;
  UNSAFELabelStyling?: string;
}

export const LabeledCheckbox = forwardRef<
  HTMLButtonElement,
  LabeledCheckboxProps & { checkboxClass?: string }
>((props: LabeledCheckboxProps, ref) => {
  const checkboxId = useHtmlId();
  const color = props.color ?? "magenta";

  return (
    <div
      data-test={`checkbox-${checkboxId}`}
      className={checkboxStyles({ color })}
    >
      <Checkbox
        ref={ref}
        onClick={props.onClick}
        checked={props.value}
        onChange={(e) => props.onChange?.(!!e)}
        id={checkboxId}
        color={color}
        size={props.size}
        disabled={props.disabled}
      />
      <InputLabel
        labelMethod={normalizeLabelProp(props.label)}
        isElementInteractive
        id={checkboxId}
        UNSAFELabelStyling={props.UNSAFELabelStyling}
      />
    </div>
  );
});
